/**
 * Typed egress configuration model (epic NaDouTongToken/protocol#473, R1).
 *
 * Replaces the legacy dialect blob (type literal + options JSON) with the
 * provider-lane operational contract
 * `{kind, lane, mode?, target?, fixups?, matching, admission?, adapter?, openaiUserMaxLength?, openaiPromptCacheKey?}`:
 *
 * - `kind` is derived from `lane` (`cli-disguise` vs `api-direct`).
 * - `mode` is the egress mode axis (`passthrough | fixup | assemble`).
 *   cli-disguise lanes must declare it explicitly; api-direct lanes
 *   default to `passthrough`.
 * - `target` is the assemble target: `latest` or a shape-class id from
 *   the lane catalog published by `/v1/capabilities`. Required exactly
 *   when `mode === 'assemble'`.
 * - `fixups` is the A.2 opt-in fixup pool, valid exactly when
 *   `mode === 'fixup'`.
 * - `matching` is the version/surface graceful-degradation axis
 *   (default `best-effort`), strictly separate from `admission`.
 * - `admission` is the identity-admission axis (cli-disguise lanes, plus one
 *   api-direct exception: an `anthropic` lane may carry `require-cc-black`
 *   armed with only `oneTokenMeasurement`, so a fixture that authenticates
 *   with `x-api-key` can answer a desktop measurement locally).
 * - `adapter` is the per-lane private slot; `adapter.codex` is defined
 *   here (#830) and consumed by the codex lane runtime (#831),
 *   `adapter.openai` by the openai lane's images egress (#1886),
 *   request-field stripping (`dropRequestFields`), the opt-in
 *   Responses→Chat conversion (`translateResponsesToChat`), forced-thinking
 *   disable repair (`repairForcedThinkingDisable`), and
 *   `adapter.arkVideo` by the asynchronous task lane's dial and settlement
 *   (#1883 second half). Each slot is valid only on its own lane.
 * - `openaiUserMaxLength` is an opt-in cap on the injected OpenAI/Codex
 *   `user` value. Absent means no cap — byte-identical to the uncapped form.
 * - `openaiPromptCacheKey` is an opt-in injection of OpenAI
 *   `prompt_cache_key` on the openai lane. Absent means byte-identical to
 *   today; the key is only valid on the `openai` lane.
 *
 * Parsing is strict: unknown keys anywhere in the document are rejected
 * (fail-fast), matching the migration's unknown-key policy.
 */

export type EgressKind = 'cli-disguise' | 'api-direct';

export type EgressCliLane = 'claude-code' | 'codex';
/**
 * `ark_video_v1` is the asynchronous task lane (#1883 second half): an
 * api-direct lane whose name is the upstream WIRE FAMILY the tasks are spoken
 * in (the Ark v1 video task grammar — submit/status/parameters as measured in
 * #1874), matching the `'ark_video_v1'` registration-protocol axis the same
 * way `openai` the lane matches `openai` the protocol (both axes follow the
 * `<protocol family>_<form>_<version>` naming rule, owner 2026-08-17). It is
 * served over OpenAI-compatible bases
 * (`SERVING_UPSTREAM_PROTOCOL.ark_video_v1 === 'openai'`), so
 * `upstreamProtocolForLane` maps it onto the openai ladder; the lane member
 * exists because the task lane's per-supply calibration (`adapter.arkVideo`)
 * must have a lane of its own to be valid on — mounting it under
 * `adapter.openai` would put task-lane calibration on every synchronous
 * openai upstream's document.
 *
 * `grok_video_v1` is the sibling Grok video lane (#1949 B / #2138):
 * identity-mapped onto UpstreamProtocol grok_video_v1; `ark_video_v1`
 * remains the openai-ladder exception. Own adapter slot
 * (`adapter.grokVideo`). Do NOT fold Grok into `adapter.arkVideo` — that
 * slot's comment exists to stop silent mounting of the wrong grammar.
 */
export type EgressApiLane =
  | 'anthropic'
  | 'openai'
  | 'gemini'
  | 'ark_video_v1'
  | 'grok_video_v1'
  | 'grok_image_v1';
export type EgressLane = EgressCliLane | EgressApiLane;

export type EgressLaneMode = 'passthrough' | 'fixup' | 'assemble';

/**
 * Our assemble behavior, not the Claude Code catalog (`target`).
 * Absent on a stored document is `v1` (the historical renderer).
 * `v2` is the shape path: no router `cch`, best-effort for incomplete
 * bodies, and a default reject for one-shot traffic with no cache breakpoint.
 */
export type EgressCcAssembleVersion = 'v1' | 'v2';

export type EgressMatching = 'best-effort' | 'strict';

export type EgressFingerprintFixup = 'recompute';
export type EgressCchFixup = 'ensure' | 'recompute';

export interface EgressFixups {
  readonly fingerprint?: EgressFingerprintFixup;
  readonly cch?: EgressCchFixup;
}

export type EgressAdmissionMode =
  | 'require-cc'
  | 'require-cc-black'
  | 'allow-anthropic'
  | 'allow-any-supported'
  | 'direct';

export type EgressAdmissionLevel =
  | 'reject'
  | 'partial'
  | 'standard'
  | 'unknown-new'
  | 'genuine';

export type EgressSurfaceBucket =
  | 'model-validation'
  | 'title-gen'
  | 'agent-sdk'
  | 'subagent-isolated'
  | 'main-loop'
  | 'unknown';

export type EgressEntrypointClass =
  | 'first-party-interactive'
  | 'first-party-managed'
  | 'agent-sdk'
  | 'integration'
  | 'internal';

/** The seven-field matched-partial integrity policy (epic AC#8). */
export interface EgressMatchedPartialIntegrity {
  readonly targetLevel?: EgressAdmissionLevel;
  readonly requireFingerprintMatch?: boolean;
  readonly requireManifestMatch?: boolean;
  readonly requireNoConflicts?: boolean;
  readonly allowedSurfaceBuckets?: readonly EgressSurfaceBucket[];
  readonly allowedEntrypointClasses?: readonly EgressEntrypointClass[];
  readonly allowedEntrypoints?: readonly string[];
}

/**
 * What a fired `require-cc-black` rule does to the request (router#1609).
 *
 * The action is per rule, not per mode: the operator picks how loudly each
 * named class is refused. `synthesize-200` answers locally with a complete
 * non-streaming Anthropic Message and settles at zero, `model-unavailable`
 * is the 404 `MODEL_NOT_AVAILABLE` the router already uses when nothing here
 * can serve a model, and `reject` is a 403 `REQUEST_NOT_SUPPORTED`.
 */
export type EgressAdmissionBlacklistAction =
  | 'synthesize-200'
  | 'count-tokens'
  | 'model-unavailable'
  | 'reject';

/** One blacklist rule. Present means armed; absent means the rule is OFF. */
export interface EgressAdmissionBlacklistRule {
  readonly action: EgressAdmissionBlacklistAction;
}

/**
 * The `require-cc-black` rule set (router#1609, #1625).
 *
 * Every key is optional and an absent key is an inert rule, so
 * `blacklist` absent, `{}`, and every rule omitted are the same
 * configuration: a mode that refuses nothing. That is the shipped default,
 * and the mode it must be indistinguishable from is **`allow-any-supported`**
 * — not `direct`.
 *
 * The anchor matters and was corrected once already. Production's live cc-lane
 * profiles run `allow-any-supported`, so `direct` is not what an adopting
 * profile is switching away from, and anchoring on it would have retired the
 * `unknown`-client refusal those profiles apply and turned an `assemble`
 * profile from rendering into raw passthrough — a loosening, on the
 * production admission path, wearing the word "parity".
 *
 * The two modes genuinely differ, so do not "fix" the code to match a doc
 * comment claiming otherwise: `ccSidecarAdmissionGateConfigured`
 * (`packages/egress-runtime/src/admission-gates.ts`) leaves the sidecar
 * recognize-step gate unarmed for `direct` and armed for `require-cc-black`,
 * alongside every other mode — including an absent `admission` block, which
 * resolves to `allow-any-supported` and is armed with it (#1383). `direct` is
 * the only unarmed spelling.
 *
 * What is actually proven is what
 * `apps/gateway-worker/test/cc-admission-black-parity.test.ts` compares:
 * every admission-visible output of a `require-cc-black` config against the
 * same config on `allow-any-supported`, across both spellings of "no rule
 * armed", all three lane modes and every client identity the header detector
 * can return. It is a comparison of resolved admission outputs, not a claim
 * about the whole request path.
 *
 * - `bareApiOnCcLane` — the request reached a `claude-code` lane while its
 *   resolved client identity is `anthropic-api`, i.e. bare Anthropic API
 *   traffic on a lane built for Claude Code clients.
 * - `agentSdkOnCcLane` — the request reached a `claude-code` lane carrying a
 *   Claude Code user-agent whose parsed entrypoint is in the `agent-sdk`
 *   class (`sdk-cli`, `sdk-ts`, `sdk-py`, `claude-code-action`,
 *   `claude-code-github-action`), i.e. programmatic Agent SDK traffic on a
 *   lane built for the interactive client. `cli`, `local-agent`,
 *   `claude-desktop`, `claude-desktop-*` and `claude-vscode` are other
 *   classes and are left alone. The rule reads the CLASS, not a list of
 *   codes, so a future SDK entrypoint added to the taxonomy is covered
 *   without editing the rule (router#1625).
 * - `missingCcBetaHeader` — the request reached a `claude-code` lane
 *   carrying no `anthropic-beta` header at all.
 * - `oneTokenMeasurement` — a Claude Code client (`user-agent` contains
 *   `claude-cli/` or `anthropic-beta` contains `claude-code`) posted a
 *   non-streaming measurement: `max_tokens` exactly 1, no system prompt, one
 *   user message. Armed with `count-tokens` so the pool answers it locally
 *   instead of spending a slot. The action is not `synthesize-200`: that
 *   estimate is bytes/3 and under-counts a tool list.
 * - `firstPartyHealthProbe` — the request was issued by a browser engine's own
 *   network stack (all three of `sec-fetch-dest` / `sec-fetch-mode` /
 *   `sec-fetch-site` present, which Chromium injects and application code
 *   cannot suppress) AND has the shape of Claude Desktop's connection
 *   self-check: `max_tokens: 1`, one user message whose content is the string
 *   `"."`, no system prompt, no tools, not streaming. Unlike the other three
 *   this rule exists to be armed with `synthesize-200`: the probe asks "does
 *   this lane answer", so answering it locally is both correct and free
 *   (router#1768).
 *
 * `bareApiOnCcLane` and `missingCcBetaHeader` both carry a browser-issued
 * exemption for the same reason (#1768): Claude Desktop's Electron main
 * process sends no `claude-cli/` user-agent and no `anthropic-beta`, so
 * between them the two rules were refusing an entire first-party population.
 * `agentSdkOnCcLane` needs no exemption — it requires a cc user-agent, and
 * `User-Agent` is a forbidden header for `fetch()`, so a browser cannot send
 * one.
 *
 * ── Four limits an operator has to know before ticking anything ────────────
 *
 * 1. **Anthropic-native ingress only.** The rules are evaluated only for
 *    tenants speaking the Anthropic wire protocol. A request that arrives on
 *    the OpenAI or Gemini ingress and is routed onto a `claude-code`
 *    candidate is never judged, so `missingCcBetaHeader` will NOT refuse it —
 *    and those clients never send `anthropic-beta`, so without this limit the
 *    rule would refuse every one of them by construction. Both actions'
 *    answers are Anthropic shapes those ingresses cannot render either.
 * 2. **A refusal the lane already applies wins.** If the profile's admission
 *    mode has already rejected the client, no rule runs. A blacklist can
 *    refuse more; it can never admit something that was already refused, so
 *    `synthesize-200` cannot turn an existing rejection into a 200.
 * 3. **The rules carry no namespace dimension; the config's location is the
 *    scope.** `bareApiOnCcLane` names a client identity on a lane, not a SKU
 *    namespace, so wherever the block is stored it fires for every request
 *    that reaches it. On an `egress_profiles` row that is every key-channel
 *    binding underneath; on a binding's `egress_config_override_json` it is
 *    that one `(key_bucket_id, channel_id)` pair.
 *
 *    This is deliberate, and the alternative was considered and rejected.
 *    #1609's corpus analysis proposed narrowing `bareApiOnCcLane` itself with
 *    a `namespace === 'claude-code'` condition, because production runs the
 *    `claude-code` and `kiro` namespaces off one profile and `kiro`'s bare-API
 *    traffic is correct by design and fully successful — so an unnarrowed
 *    profile-level rule would refuse working traffic to fix a different
 *    namespace's problem. Per-binding arming answers the same question at the
 *    granularity the operator actually holds: a binding is the row admission
 *    already attaches to, it separates those two namespaces because they sit
 *    on different upstream channels, and it needs no new predicate that would
 *    then have to be kept in step with however namespaces are carved next.
 *    A namespace condition would additionally have been silent — a rule that
 *    looks armed and never fires — where a scope is visible in the document's
 *    own location.
 * 4. **Every rule refuses MISUSE, not an attacker; none of them is a security
 *    boundary.** Every one of them reads request headers at face value, and
 *    headers are forgeable. `bareApiOnCcLane` is defeated by sending a cc
 *    user-agent, or since #1768 by adding three `sec-fetch-*` headers,
 *    `missingCcBetaHeader` by sending any `anthropic-beta` value, and
 *    `agentSdkOnCcLane` by changing one substring of the user-agent — the
 *    entrypoint is a self-declared field that
 *    `packages/protocol-engine/src/identity/anthropic.ts` describes as being
 *    for "accounting / audit, never for security decisions (since UA is
 *    forgeable)". This is not a weakness the newest rule introduces; it is the
 *    shape of the whole feature. Arm a rule to stop a class of traffic that is
 *    on the wrong lane by accident or by habit. Do not arm one expecting it to
 *    hold against a caller who wants through, and do not build an entitlement
 *    on top of one (router#1625).
 */
export interface EgressAdmissionBlacklist {
  readonly oneTokenMeasurement?: EgressAdmissionBlacklistRule;
  readonly firstPartyHealthProbe?: EgressAdmissionBlacklistRule;
  readonly bareApiOnCcLane?: EgressAdmissionBlacklistRule;
  readonly agentSdkOnCcLane?: EgressAdmissionBlacklistRule;
  readonly missingCcBetaHeader?: EgressAdmissionBlacklistRule;
}

export interface EgressAdmission {
  readonly mode: EgressAdmissionMode;
  readonly minimumLevel?: EgressAdmissionLevel;
  readonly matchedPartialIntegrity?: EgressMatchedPartialIntegrity;
  readonly modelAllowlist?: readonly string[];
  readonly blacklist?: EgressAdmissionBlacklist;
}

export type CodexAdapterBackend = 'api' | 'oauth';
export type CodexAdapterTransport = 'http' | 'wss';

/**
 * `adapter.codex` — the codex lane private slot (defined by R1, consumed
 * by #831). Adapter detail never enters the shared axes or the published
 * shape-class catalog.
 */
export interface CodexAdapterConfig {
  readonly backend: CodexAdapterBackend;
  readonly transport: CodexAdapterTransport;
  readonly store?: boolean;
  readonly betaFeatures?: readonly string[];
  readonly originatorOverride?: string;
  readonly dropMaxOutputTokens?: boolean;
  /**
   * Opt-in (#2815, #2808): drop `type:"reasoning"` input items that carry no
   * `encrypted_content` on stateless (`store:false`, no
   * `previous_response_id`) Responses requests. Absent/false = the #2808
   * drop does not run (reasoning items are forwarded). Only turn this on for codex upstreams with OpenAI-native
   * Responses semantics (the account pool); third-party Responses upstreams
   * may resolve such items differently.
   */
  readonly dropUnresolvableReasoningItems?: boolean;
  readonly baseURL?: string;
}

export type OpenAIAdapterImagesEgress = 'via-responses' | 'native';

/**
 * `adapter.openai` — the openai lane private slot (#1886).
 *
 * `images` says which wire an `/v1/images/*` ingress leaves this upstream on,
 * and it is a per-upstream fact rather than a property of the protocol:
 *
 * - `via-responses` (the default, and what every stored document means by
 *   saying nothing) restates the request as a Responses turn carrying the
 *   built-in `image_generation` tool. That codec's own header records what it
 *   costs — the prompt comes back rewritten, `quality` is decided upstream,
 *   and `size` does not survive in any form.
 * - `native` forwards `POST /v1/images/generations` untouched, so `size` and
 *   `quality` reach the upstream verbatim.
 *
 * It is opt-in per upstream, never inferred, because "a native Images endpoint
 * honours its parameters" is not a universal fact: #1546 measured one that
 * accepts `999x999` and generates anyway. Turning this on for an upstream
 * whose binding has not been measured would bind a price to a parameter the
 * upstream ignores, which is the exact miscooking #1883 shut the tier pricing
 * off to avoid.
 *
 * The two settings are also not the same product — `via-responses` delivers a
 * fixed-quality image from a rewritten prompt — so they belong on different
 * SKUs, not on one SKU whose delivery depends on this flag.
 *
 * `vendor` names the upstream adapter whose measured quirks apply to THIS
 * upstream (#1872). It is the same kind of statement as `images` and is
 * declared the same way — a per-upstream fact, opt-in, never inferred — for
 * the same reason: the protocol an upstream speaks does not tell you what it
 * does to an error body or to a usage object. Saying nothing means "no
 * measured quirks", which is byte-for-byte today's behavior.
 *
 * It is deliberately NOT `provider`, `vendor` (the SKU's) or `channel_slug`.
 * The SKU vendor names who built the model, not who resold it; the channel
 * slug is operator-editable free text, so a rename would silently drop the
 * quirks. This value is a closed enum whose members each cite a measurement.
 *
 * Scope, stated plainly because the slot's name understates it: living under
 * `adapter.openai` makes this per-(openai-lane upstream), not per-upstream in
 * general. Every upstream measured so far speaks the OpenAI wire, so nothing
 * is currently out of reach — but an anthropic- or gemini-lane upstream with
 * its own quirks would need a sibling slot on its own lane, not a widening of
 * this one. Lane slots are validated per lane on purpose (see `parseAdapter`).
 *
 * `dropRequestFields` is an opt-in list of dotted JSON paths stripped from
 * the outbound OpenAI request body (chat completions and responses). Absent
 * means byte-identical to today's passthrough. It exists because some
 * OpenAI-compatible upstreams (weimeta `/v1/responses`) use strict Go JSON
 * decoding and 400 on official fields they have not implemented
 * (`reasoning.summary`). Qwen and OpenAI accept the same field. The gateway
 * absorbs the difference per binding rather than asking every client to
 * omit it. Empty parents are kept (`reasoning: {}` is legal upstream).
 *
 * `repairToolCallArguments` is an opt-in repair of truncated
 * `messages[].tool_calls[].function.arguments` on outbound chat-completions
 * bodies. Same posture as `dropRequestFields` — a per-binding statement about
 * a measured upstream — for the same kind of reason: SGLang-served upstreams
 * (supernova, measured 2026-09-17) `json.loads` every historical tool call
 * before rendering the GLM chat template and answer 400 `Assistant tool call
 * function.arguments must be valid JSON.`; official Zhipu, PPIO and Bailian
 * pass the same body through. A client that trims its history by cutting
 * strings to a fixed length leaves such a call in every later turn, so on the
 * strict upstream the conversation fails permanently. The repair closes a
 * well-formed prefix (missing quote / braces only), drops a key fragment the
 * cut left without a value, and refuses anything that would drop a key that
 * had a value or invent an empty one; refused calls go out unchanged. Absent /
 * false is byte-identical to today. Chat completions only — Responses
 * `function_call` items are not touched.
 *
 * `repairForcedThinkingDisable` is an opt-in `{ models, ratio }` repair.
 * It fires only when the client is trying to *turn thinking off* on a model
 * that cannot disable thinking (forced-thinking). The dialects are
 * `enable_thinking: false`, `chat_template_kwargs.enable_thinking: false`,
 * `thinking.type: "disabled"`, `reasoning.enabled: false`,
 * `reasoning_effort: "none"`. Default thinking, and an explicit
 * `low`/`medium`/`high` with no disable dialect, are left alone.
 * `models` are outbound names; `ratio` is a keep fraction in `[0, 1]`
 * (FNV-1a of `request_id`, same unit as lifecycle sampling: 0 never,
 * 1 always). A kept match rewrites to `reasoning_effort: "low"` — the
 * shortest legal budget — and strips the disable-shaped fields.
 * Missing `request_id` at a partial ratio fails closed (no rewrite).
 * Measured 2026-10-21 on SGLang-served GLM-5.3 (supernova): the disable
 * cocktail 400s with "use reasoning_effort='low'"; `enable_thinking: false`
 * alone is ignored and the model thinks at the default budget. Absent means
 * byte-identical passthrough. Chat completions only.
 */
export interface OpenAIAdapterConfig {
  readonly images?: OpenAIAdapterImagesEgress;
  readonly vendor?: UpstreamVendorAdapterId;
  /**
   * Dotted paths to delete from the outbound OpenAI JSON body
   * (e.g. `reasoning.summary`, `store`). 1–8 unique paths.
   */
  readonly dropRequestFields?: readonly string[];
  /**
   * Opt-in: translate `/v1/responses` clients onto Chat Completions on this
   * openai (chat) lane. Absent / false is identity — the same plan origin/main
   * emits. Only an explicit `true` converts (`input`→`messages`,
   * `max_output_tokens`→`max_tokens`, path `/v1/chat/completions`).
   */
  readonly translateResponsesToChat?: boolean;
  /**
   * Opt-in: close truncated `tool_calls[].function.arguments` JSON on
   * outbound chat-completions bodies. Absent / false is identity. Only an
   * explicit `true` repairs; ambiguous truncations are still passed through.
   */
  readonly repairToolCallArguments?: boolean;
  /**
   * Forced-thinking disable repair. `models` are outbound names (1–8);
   * `ratio` is the keep fraction in `[0, 1]` (0 = identity, 1 = every match).
   * Only disable-thinking dialects are rewritten; other bodies are identity.
   */
  readonly repairForcedThinkingDisable?: OpenAIRepairForcedThinkingDisable;
}

export interface OpenAIRepairForcedThinkingDisable {
  readonly models: readonly string[];
  readonly ratio: number;
}

/**
 * Upstreams with measured, reproducible response-side quirks (#1872, evidence
 * archived in #1874).
 *
 * - `ucloud` — restates an upstream 4xx as an HTTP 500 `internal_error` /
 *   `model_server_error` and buries the real class as escaped JSON inside
 *   `message` (measured 2026-08-11/12 on dev, real paid traffic).
 *
 * Adding a member is adding a measurement, not a guess: the adapter it selects
 * must cite the sample it was written from.
 *
 * - `ucloud` — Ark task grammar (UCloud envelope). Valid on `adapter.arkVideo` only.
 * - `kkhub` — Ark-native v3 grammar under `/api` (measured 2026-08-28). Valid
 *   on `adapter.arkVideo` only.
 * - `jojokey` — Ark-native flat envelope under `/v1/video-cn` (measured
 *   2026-08-30 against video.jojokey.com). Valid on `adapter.arkVideo` only.
 * - `jojokey-intl` — JoJoKey overseas unified line `POST /v1/videos`
 *   (measured 2026-09-02 against video.jojokey.com, `minimax-A` Agnes
 *   envelope). Valid on `adapter.arkVideo` only. Named `-intl` rather than
 *   a model id so the adapter is the lane, not one SKU.
 * - `pool` — Pool `/xai/v1/video` grammar (pool #4763 / ADR-039). Valid on
 *   `adapter.grokVideo` only. A vendor on the wrong slot is refused at parse.
 */
export type ArkVideoVendorAdapterId = 'ucloud' | 'kkhub' | 'jojokey' | 'jojokey-intl';
export type GrokVideoVendorAdapterId = 'pool';
export type GrokImageVendorAdapterId = 'pool';
export type UpstreamVendorAdapterId =
  | ArkVideoVendorAdapterId
  | GrokVideoVendorAdapterId
  | GrokImageVendorAdapterId;

/**
 * `adapter.arkVideo` — the task lane's private slot (#1883 second half).
 *
 * This slot is HOW A TASK-SERVING UPSTREAM IS MOUNTED. The owner's model:
 * protocol is the ingress form, profile is the billing form, and the upstream
 * declares its in/out adaptation on its egress binding. Before this slot the
 * task lane's adapter was implied by `adapter.openai.vendor` — a slot defined
 * for the SYNCHRONOUS openai lane's response quirks — and a channel with no
 * binding at all silently fell back to a synthesized profile, so nothing an
 * operator could see or edit stated which task grammar a dial would use. The
 * task ingress now refuses to route a channel whose binding does not resolve
 * to this lane (fail-closed, naming what is missing), and this slot is the
 * only place the answer lives.
 *
 * - `vendor` names the upstream adapter whose measured task grammar and
 *   response quirks apply — the same closed, measurement-backed enum
 *   `adapter.openai.vendor` uses ({@link UpstreamVendorAdapterId}). REQUIRED:
 *   an ark_video_v1-lane document without one would name a lane and no
 *   grammar, which is a config that promises tasks and cannot dial any.
 * - `deliveryQuantityCoefficient` is the per-supply delivered-quantity
 *   calibration the ADR-012 §2 amendment left a seat for: settlement reads
 *   the unit-lane amount as `terminal report × coefficient`. Absent means
 *   1.0 — the identity pass-through, byte-identical to today's behavior.
 *   It exists because "allow a factor, forbid free-form reporting" (owner):
 *   a supply whose terminal figure is systematically scaled can be corrected
 *   by an operator-visible number on the binding, instead of by trusting the
 *   supply to restate itself. Strictly positive and finite; it is frozen onto
 *   the task row at submit beside the procurement snapshot, for the same
 *   reason — an operator re-calibrating mid-generation must not re-price a
 *   task already dialled.
 * - `parameterSpellings` is the canonical-slot → upstream-spelling map
 *   (#1883 ④): outer key is a canonical parameter name, inner map takes the
 *   canonical value to the spelling THIS upstream wants on the wire (e.g.
 *   `{"resolution": {"480p": "480"}}` for a supply that spells resolutions
 *   bare). Empty or absent means pass-through — today's behavior for every
 *   supply, since the measured one accepts the canonical spellings verbatim.
 *   It is a structured seat rather than a future guess: the moment a second
 *   reseller of this grammar respells one enum value, the fix is binding
 *   data, not a code branch.
 * - `assetRecovery` is the JoJoKey-only CN real-person image recovery
 *   (register the refused first-frame into the domestic asset library,
 *   then resubmit with `asset://`). Default absent/false. It is a product
 *   switch, not a grammar quirk: the recovery does I/O and adds latency,
 *   so an operator must be able to turn it off without a deploy. `true`
 *   is refused on any vendor other than `jojokey`.
 */
export interface ArkVideoAdapterConfig {
  readonly vendor: ArkVideoVendorAdapterId;
  readonly deliveryQuantityCoefficient?: number;
  readonly parameterSpellings?: Readonly<Record<string, Readonly<Record<string, string>>>>;
  readonly assetRecovery?: boolean;
}

/**
 * `adapter.grokVideo` — the Grok video lane's private slot (#1949 B / #2138).
 *
 * Mirror of {@link ArkVideoAdapterConfig} with a different measured grammar:
 * `vendor` selects the Pool `/xai/v1/video` adapter, and
 * `deliveryQuantityCoefficient` has the same semantics as ark (terminal
 * seconds × coefficient, default 1.0, frozen onto the task row at submit).
 * Valid only on lane `grok_video_v1`. `'ucloud'`, `'kkhub'`, `'jojokey'`
 * and `'jojokey-intl'` are refused here; `'pool'` is refused on
 * `adapter.arkVideo`.
 */
export interface GrokVideoAdapterConfig {
  readonly vendor: GrokVideoVendorAdapterId;
  readonly deliveryQuantityCoefficient?: number;
}

/**
 * `adapter.grokImage` — the Grok still-image lane's private slot (#2190).
 *
 * Valid only on lane `grok_image_v1`. `vendor: 'pool'` selects the Pool
 * `{origin}/xai/v1/images/generations` adapter. Do not fold this into
 * `adapter.grokVideo` or `adapter.openai.images`.
 */
export interface GrokImageAdapterConfig {
  readonly vendor: GrokImageVendorAdapterId;
}

export interface EgressAdapterConfig {
  readonly codex?: CodexAdapterConfig;
  readonly openai?: OpenAIAdapterConfig;
  readonly arkVideo?: ArkVideoAdapterConfig;
  readonly grokVideo?: GrokVideoAdapterConfig;
  readonly grokImage?: GrokImageAdapterConfig;
}

/**
 * `firewall` — a **reference** to a stored firewall ruleset (#1758).
 *
 * The document names a ruleset and, optionally, pins a version:
 *
 * ```jsonc
 * "firewall": { "rulesetId": "fw_ab12…" }              // follows active_version
 * "firewall": { "rulesetId": "fw_ab12…", "version": 7 } // frozen at a version
 * ```
 *
 * Rules are never inlined here. `resolveEgressConfig` replaces the whole
 * document when a binding overrides a profile, so an inlined chain would be
 * copied into every override, fossilize at the moment of the copy, and detach
 * from the ruleset it was authored against — the review, replay and version
 * history that make a rule change safe all live on the ruleset, not on the
 * profile. A reference keeps exactly one writable copy of the judgement.
 */
export interface EgressFirewallRef {
  readonly rulesetId: string;
  /** Absent = follow the ruleset's `active_version`. Present = frozen. */
  readonly version?: number;
}

export interface EgressConfig {
  readonly kind: EgressKind;
  readonly lane: EgressLane;
  readonly mode: EgressLaneMode;
  /**
   * Claude Code assemble behavior pin. Only valid when `lane` is
   * `claude-code` and `mode` is `assemble`. Absent means `v1`.
   */
  readonly assembleVersion?: EgressCcAssembleVersion;
  readonly target?: string;
  readonly fixups?: EgressFixups;
  readonly matching: EgressMatching;
  readonly admission?: EgressAdmission;
  readonly firewall?: EgressFirewallRef;
  readonly adapter?: EgressAdapterConfig;
  /** Opt-in max length of the injected OpenAI/Codex `user` field. Absent = no cap. */
  readonly openaiUserMaxLength?: number;
  /**
   * Opt-in OpenAI `prompt_cache_key` injection. Absent = off — byte-identical
   * to today. Only valid on the `openai` lane.
   */
  readonly openaiPromptCacheKey?: boolean;
}

export const EGRESS_LANES = [
  'claude-code',
  'codex',
  'anthropic',
  'openai',
  'gemini',
  'ark_video_v1',
  'grok_video_v1',
  'grok_image_v1',
] as const satisfies readonly EgressLane[];

export const EGRESS_ASSEMBLE_TARGET_LATEST = 'latest';

const EGRESS_LANE_MODES = ['passthrough', 'fixup', 'assemble'] as const;
const EGRESS_CC_ASSEMBLE_VERSIONS = ['v1', 'v2'] as const;
const EGRESS_MATCHINGS = ['best-effort', 'strict'] as const;
const EGRESS_ADMISSION_MODES = [
  'require-cc',
  'require-cc-black',
  'allow-anthropic',
  'allow-any-supported',
  'direct',
] as const;
const EGRESS_ADMISSION_LEVELS = [
  'reject',
  'partial',
  'standard',
  'unknown-new',
  'genuine',
] as const;
const EGRESS_SURFACE_BUCKETS = [
  'model-validation',
  'title-gen',
  'agent-sdk',
  'subagent-isolated',
  'main-loop',
  'unknown',
] as const;
const EGRESS_ENTRYPOINT_CLASSES = [
  'first-party-interactive',
  'first-party-managed',
  'agent-sdk',
  'integration',
  'internal',
] as const;
const CODEX_ADAPTER_BACKENDS = ['api', 'oauth'] as const;
const CODEX_ADAPTER_TRANSPORTS = ['http', 'wss'] as const;
const EGRESS_FINGERPRINT_FIXUPS = ['recompute'] as const;
const EGRESS_CCH_FIXUPS = ['ensure', 'recompute'] as const;

const EGRESS_CONFIG_KEYS = [
  'kind',
  'lane',
  'mode',
  'assembleVersion',
  'target',
  'fixups',
  'matching',
  'admission',
  'firewall',
  'adapter',
  'openaiUserMaxLength',
  'openaiPromptCacheKey',
] as const;
const EGRESS_FIREWALL_KEYS = ['rulesetId', 'version'] as const;
/**
 * Keys that spell an inlined rule chain. Rejected by name, with a reason,
 * rather than folded into the generic unknown-key message: an operator who
 * writes `"firewall": { "rules": [...] }` has a coherent mental model that
 * happens to be wrong, and "unknown key" teaches them nothing about why.
 */
const EGRESS_FIREWALL_INLINE_KEYS = ['rules', 'ruleset', 'chain'] as const;
const EGRESS_FIXUPS_KEYS = ['fingerprint', 'cch'] as const;
const EGRESS_ADMISSION_KEYS = [
  'mode',
  'minimumLevel',
  'matchedPartialIntegrity',
  // allowPlainAnthropic* — retired (#1311); tolerated below, never emitted.
  'allowPlainAnthropicIdentityKinds',
  'allowPlainAnthropicEntrypointClasses',
  'modelAllowlist',
  // `probeShortCircuit` — retired (#1609), and deliberately NOT tolerated the
  // way the two `allowPlainAnthropic*` keys above are.
  //
  // The difference is whether a stored document carries it. Those two shipped
  // as observability fields that operators actually wrote, so silently
  // dropping them is the only way old profiles keep parsing. `probeShortCircuit`
  // shipped in #1603 as a standby switch and was never armed on any profile in
  // any environment, so nothing in storage carries it and the strict
  // unknown-key rejection cannot brick a live document. Rejecting is the more
  // honest answer for a key whose runtime is gone: an operator who writes it
  // by hand learns immediately, at `$.admission.probeShortCircuit`, instead of
  // believing an intercept is armed that no code reads.
  'blacklist',
] as const;
// In the runtime's evaluation order (`CC_BLACKLIST_RULE_ORDER` in
// `packages/egress-runtime/src/cc-policy.ts`), so a parsed document serializes
// its rules in the order they are actually judged. A stored document that
// predates `firstPartyHealthProbe` is unaffected: an absent key is skipped, so
// the surviving keys keep both their relative order and their bytes.
const EGRESS_ADMISSION_BLACKLIST_RULES = [
  'oneTokenMeasurement',
  'firstPartyHealthProbe',
  'bareApiOnCcLane',
  'agentSdkOnCcLane',
  'missingCcBetaHeader',
] as const;
const EGRESS_ADMISSION_BLACKLIST_RULE_KEYS = ['action'] as const;
const EGRESS_ADMISSION_BLACKLIST_ACTIONS = [
  'synthesize-200',
  'count-tokens',
  'model-unavailable',
  'reject',
] as const;
const EGRESS_MATCHED_PARTIAL_KEYS = [
  'targetLevel',
  'requireFingerprintMatch',
  'requireManifestMatch',
  'requireNoConflicts',
  'allowedSurfaceBuckets',
  'allowedEntrypointClasses',
  'allowedEntrypoints',
] as const;
const EGRESS_ADAPTER_KEYS = ['codex', 'openai', 'arkVideo', 'grokVideo', 'grokImage'] as const;
const OPENAI_ADAPTER_KEYS = [
  'images',
  'vendor',
  'dropRequestFields',
  'translateResponsesToChat',
  'repairToolCallArguments',
  'repairForcedThinkingDisable',
] as const;
const OPENAI_DROP_REQUEST_FIELD_PATH = /^[A-Za-z0-9_]+(\.[A-Za-z0-9_]+)*$/;
const OPENAI_DROP_REQUEST_FIELDS_MIN = 1;
const OPENAI_DROP_REQUEST_FIELDS_MAX = 8;
const OPENAI_REPAIR_FORCED_THINKING_DISABLE_MODEL = /^[A-Za-z0-9][A-Za-z0-9._/-]{0,127}$/;
const OPENAI_REPAIR_FORCED_THINKING_DISABLE_MIN = 1;
const OPENAI_REPAIR_FORCED_THINKING_DISABLE_MAX = 8;
const OPENAI_REPAIR_FORCED_THINKING_DISABLE_KEYS = ['models', 'ratio'] as const;
const ARK_VIDEO_ADAPTER_KEYS = [
  'vendor',
  'deliveryQuantityCoefficient',
  'parameterSpellings',
  'assetRecovery',
] as const;
const GROK_VIDEO_ADAPTER_KEYS = ['vendor', 'deliveryQuantityCoefficient'] as const;
const GROK_IMAGE_ADAPTER_KEYS = ['vendor'] as const;
const OPENAI_ADAPTER_IMAGES_EGRESS = ['via-responses', 'native'] as const;
/**
 * The adapter ids, as data.
 *
 * Exported because {@link UpstreamVendorAdapterId} is not checkable at runtime
 * and the ids arrive from a TEXT column (`generation_tasks.upstream_adapter_id`)
 * as well as from a parsed egress document. A reader with only the type has to
 * assert, which is how an unknown id becomes an `undefined` adapter that the
 * signature says cannot exist.
 *
 * Slot-specific lists are the parse authority: `'pool'` is grok-only,
 * `'ucloud'`, `'kkhub'`, `'jojokey'` and `'jojokey-intl'` are ark-only. The
 * union is what a stored TEXT column may carry.
 */
export const ARK_VIDEO_VENDOR_ADAPTER_IDS = ['ucloud', 'kkhub', 'jojokey', 'jojokey-intl'] as const satisfies
  readonly ArkVideoVendorAdapterId[];
export const GROK_VIDEO_VENDOR_ADAPTER_IDS = ['pool'] as const satisfies
  readonly GrokVideoVendorAdapterId[];
export const GROK_IMAGE_VENDOR_ADAPTER_IDS = ['pool'] as const satisfies
  readonly GrokImageVendorAdapterId[];
export const UPSTREAM_VENDOR_ADAPTER_IDS = ['ucloud', 'kkhub', 'jojokey', 'jojokey-intl', 'pool'] as const satisfies
  readonly UpstreamVendorAdapterId[];

export function isUpstreamVendorAdapterId(value: unknown): value is UpstreamVendorAdapterId {
  return isStringEnum(value, UPSTREAM_VENDOR_ADAPTER_IDS);
}
const CODEX_ADAPTER_KEYS = [
  'backend',
  'transport',
  'store',
  'betaFeatures',
  'originatorOverride',
  // 'requestCompression' — retired (#1310); tolerated below, never emitted.
  'requestCompression',
  'dropMaxOutputTokens',
  'dropUnresolvableReasoningItems',
  'baseURL',
] as const;

export class EgressConfigError extends Error {
  readonly path: string;

  constructor(path: string, message: string) {
    super(`egress config ${path}: ${message}`);
    this.name = 'EgressConfigError';
    this.path = path;
  }
}

export function isEgressLane(value: unknown): value is EgressLane {
  return typeof value === 'string' && (EGRESS_LANES as readonly string[]).includes(value);
}

export function egressKindForLane(lane: EgressLane): EgressKind {
  return lane === 'claude-code' || lane === 'codex' ? 'cli-disguise' : 'api-direct';
}

/**
 * Parse and validate an unknown value into a normalized {@link EgressConfig}.
 *
 * Normalization: `kind` is derived from `lane` (an explicit `kind` must
 * agree), `matching` defaults to `best-effort`, and an omitted `mode`
 * defaults to `passthrough` on api-direct lanes. cli-disguise lanes must
 * declare `mode` explicitly.
 */
export function parseEgressConfig(value: unknown): EgressConfig {
  const record = requireRecord(value, '$');
  assertNoUnknownKeys(record, EGRESS_CONFIG_KEYS, '$');

  const lane = record['lane'];
  if (!isEgressLane(lane)) {
    throw new EgressConfigError('$.lane', `must be one of ${EGRESS_LANES.join(', ')}`);
  }
  const kind = egressKindForLane(lane);
  if (record['kind'] !== undefined && record['kind'] !== kind) {
    throw new EgressConfigError('$.kind', `must be "${kind}" for lane "${lane}"`);
  }

  const mode = parseMode(record['mode'], lane, kind);
  const matching = parseMatching(record['matching']);
  if (lane === 'codex' && matching === 'strict') {
    // The codex assemble runtime has no strict-matching spelling yet
    // (router#831); the legacy dialect could not express it either, so no
    // stored config carries it. Reject at parse so the write face keeps the
    // pre-#831 fail-fast behavior instead of storing a config the forward
    // path cannot honor.
    throw new EgressConfigError('$.matching', 'the codex lane only supports "best-effort" matching');
  }
  const assembleVersion = parseAssembleVersion(record['assembleVersion'], lane, mode);
  const target = parseTarget(record['target'], mode);
  const fixups = parseFixups(record['fixups'], mode, lane);
  const admission = parseAdmission(record['admission'], kind, lane);
  // Deliberately not gated on lane or kind, unlike `admission`: #1751 requires
  // the codex lane (and, later, the api-direct lanes) to be able to carry a
  // firewall, and the engine is protocol-agnostic by construction. `admission`
  // and `firewall` are a serial two-stage, not alternatives — a document may
  // legally carry both.
  const firewall = parseFirewall(record['firewall']);
  const adapter = parseAdapter(record['adapter'], lane);
  const openaiUserMaxLength = parseOpenaiUserMaxLength(record['openaiUserMaxLength']);
  const openaiPromptCacheKey = parseOpenaiPromptCacheKey(record['openaiPromptCacheKey'], lane);

  if (lane === 'codex' && adapter?.codex === undefined) {
    throw new EgressConfigError('$.adapter.codex', 'is required for the codex lane');
  }
  if (lane === 'ark_video_v1' && adapter?.arkVideo === undefined) {
    // Same posture as the codex requirement: the ark_video_v1 lane exists to
    // mount a task adapter, and a document that names the lane without one
    // names no grammar the dial could use. Requiring it at the write face is
    // what makes "binding present and lane matches" imply "adapter known"
    // downstream.
    throw new EgressConfigError('$.adapter.arkVideo', 'is required for the ark_video_v1 lane');
  }
  if (lane === 'grok_video_v1' && adapter?.grokVideo === undefined) {
    throw new EgressConfigError('$.adapter.grokVideo', 'is required for the grok_video_v1 lane');
  }
  if (lane === 'grok_image_v1' && adapter?.grokImage === undefined) {
    throw new EgressConfigError('$.adapter.grokImage', 'is required for the grok_image_v1 lane');
  }

  return {
    kind,
    lane,
    mode,
    ...(assembleVersion !== undefined ? { assembleVersion } : {}),
    ...(target !== undefined ? { target } : {}),
    ...(fixups !== undefined ? { fixups } : {}),
    matching,
    ...(admission !== undefined ? { admission } : {}),
    ...(firewall !== undefined ? { firewall } : {}),
    ...(adapter !== undefined ? { adapter } : {}),
    ...(openaiUserMaxLength !== undefined ? { openaiUserMaxLength } : {}),
    ...(openaiPromptCacheKey !== undefined ? { openaiPromptCacheKey } : {}),
  };
}

export function parseEgressConfigJson(text: string | null | undefined): EgressConfig {
  if (text === null || text === undefined || text.trim().length === 0) {
    throw new EgressConfigError('$', 'config JSON must be a non-empty JSON object document');
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new EgressConfigError(
      '$',
      `config JSON is not valid JSON: ${err instanceof Error ? err.message : String(err)}`,
    );
  }
  return parseEgressConfig(parsed);
}

/** Serialize with a stable field order so stored documents diff cleanly. */
export function serializeEgressConfig(config: EgressConfig): string {
  const normalized = parseEgressConfig(config as unknown);
  return JSON.stringify(normalized);
}

function parseMode(value: unknown, lane: EgressLane, kind: EgressKind): EgressLaneMode {
  if (value === undefined) {
    if (kind === 'cli-disguise') {
      throw new EgressConfigError('$.mode', `is required for cli-disguise lane "${lane}"`);
    }
    return 'passthrough';
  }
  if (!isStringEnum(value, EGRESS_LANE_MODES)) {
    throw new EgressConfigError('$.mode', `must be one of ${EGRESS_LANE_MODES.join(', ')}`);
  }
  if (kind === 'api-direct' && value === 'assemble') {
    throw new EgressConfigError('$.mode', `"assemble" is not valid for api-direct lane "${lane}"`);
  }
  if (kind === 'api-direct' && value === 'fixup') {
    throw new EgressConfigError('$.mode', `no fixups are defined for lane "${lane}"`);
  }
  if (lane === 'codex' && value !== 'assemble') {
    throw new EgressConfigError('$.mode', 'the codex lane only supports "assemble"');
  }
  return value;
}

function parseMatching(value: unknown): EgressMatching {
  if (value === undefined) return 'best-effort';
  if (!isStringEnum(value, EGRESS_MATCHINGS)) {
    throw new EgressConfigError('$.matching', `must be one of ${EGRESS_MATCHINGS.join(', ')}`);
  }
  return value;
}

function parseAssembleVersion(
  value: unknown,
  lane: EgressLane,
  mode: EgressLaneMode,
): EgressCcAssembleVersion | undefined {
  if (value === undefined) return undefined;
  if (lane !== 'claude-code' || mode !== 'assemble') {
    throw new EgressConfigError(
      '$.assembleVersion',
      'is only valid when lane is "claude-code" and mode is "assemble"',
    );
  }
  if (!isStringEnum(value, EGRESS_CC_ASSEMBLE_VERSIONS)) {
    throw new EgressConfigError(
      '$.assembleVersion',
      `must be one of ${EGRESS_CC_ASSEMBLE_VERSIONS.join(', ')}`,
    );
  }
  return value;
}

function parseTarget(value: unknown, mode: EgressLaneMode): string | undefined {
  if (mode !== 'assemble') {
    if (value !== undefined) {
      throw new EgressConfigError('$.target', 'is only valid when mode is "assemble"');
    }
    return undefined;
  }
  if (typeof value !== 'string' || value.trim().length === 0) {
    throw new EgressConfigError(
      '$.target',
      'is required when mode is "assemble" (a shape-class id or "latest")',
    );
  }
  return value;
}

function parseFixups(
  value: unknown,
  mode: EgressLaneMode,
  lane: EgressLane,
): EgressFixups | undefined {
  if (value === undefined) {
    if (mode === 'fixup') {
      throw new EgressConfigError('$.fixups', 'is required when mode is "fixup"');
    }
    return undefined;
  }
  if (mode !== 'fixup') {
    throw new EgressConfigError('$.fixups', 'is only valid when mode is "fixup"');
  }
  if (lane !== 'claude-code') {
    throw new EgressConfigError('$.fixups', `no fixups are defined for lane "${lane}"`);
  }
  const record = requireRecord(value, '$.fixups');
  assertNoUnknownKeys(record, EGRESS_FIXUPS_KEYS, '$.fixups');
  const out: { fingerprint?: EgressFingerprintFixup; cch?: EgressCchFixup } = {};
  if (record['fingerprint'] !== undefined) {
    if (!isStringEnum(record['fingerprint'], EGRESS_FINGERPRINT_FIXUPS)) {
      throw new EgressConfigError(
        '$.fixups.fingerprint',
        `must be one of ${EGRESS_FINGERPRINT_FIXUPS.join(', ')}`,
      );
    }
    out.fingerprint = record['fingerprint'];
  }
  if (record['cch'] !== undefined) {
    if (!isStringEnum(record['cch'], EGRESS_CCH_FIXUPS)) {
      throw new EgressConfigError('$.fixups.cch', `must be one of ${EGRESS_CCH_FIXUPS.join(', ')}`);
    }
    out.cch = record['cch'];
  }
  if (out.fingerprint === undefined && out.cch === undefined) {
    throw new EgressConfigError('$.fixups', 'must select at least one fixup');
  }
  return out;
}

/**
 * The one api-direct document allowed to carry admission.
 *
 * `oneTokenMeasurement` answers a desktop count locally. A controlled-upstream
 * fixture authenticates with `x-api-key`, and the claude-code lane forces
 * `authorization-bearer`, so the rule has to be storable on the `anthropic`
 * lane. Every other rule is a cc-lane refusal and stays unstorable here: a
 * bare-API blacklist on this lane would 404 the fixture's own traffic.
 */
function isJsonObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function isAnthropicOneTokenAdmission(value: unknown, lane: EgressLane): boolean {
  if (lane !== 'anthropic' || !isJsonObject(value)) return false;
  if (value['mode'] !== 'require-cc-black') return false;
  const keys = Object.keys(value);
  if (!keys.every((key) => key === 'mode' || key === 'blacklist')) return false;
  const blacklist = value['blacklist'];
  if (!isJsonObject(blacklist)) return false;
  const rules = Object.keys(blacklist);
  return rules.length === 1 && rules[0] === 'oneTokenMeasurement';
}

function parseAdmission(
  value: unknown,
  kind: EgressKind,
  lane: EgressLane,
): EgressAdmission | undefined {
  if (value === undefined) return undefined;
  const anthropicMeasurement = isAnthropicOneTokenAdmission(value, lane);
  if (kind !== 'cli-disguise' && !anthropicMeasurement) {
    throw new EgressConfigError('$.admission', 'is only valid on cli-disguise lanes');
  }
  const record = requireRecord(value, '$.admission');
  assertNoUnknownKeys(record, EGRESS_ADMISSION_KEYS, '$.admission');
  if (!isStringEnum(record['mode'], EGRESS_ADMISSION_MODES)) {
    throw new EgressConfigError(
      '$.admission.mode',
      `must be one of ${EGRESS_ADMISSION_MODES.join(', ')}`,
    );
  }
  const mode = record['mode'];
  if (mode === 'require-cc-black' && lane !== 'claude-code' && !anthropicMeasurement) {
    // Every other rule is a cc-lane predicate — the runtime checks
    // `lane === 'claude-code'` before evaluating any of them — so on a codex
    // profile this mode is a name that promises a blacklist and can never
    // fire. It would behave exactly like the `allow-any-supported` it is
    // defined against, which is precisely what makes the document misleading
    // rather than harmful. Refuse it at the write face rather than store a
    // config whose name overstates what it does. The anthropic exception is
    // only the measurement rule, which the runtime does evaluate on that lane.
    throw new EgressConfigError(
      '$.admission.mode',
      '"require-cc-black" is only valid on the claude-code lane',
    );
  }
  const out: {
    mode: EgressAdmissionMode;
    minimumLevel?: EgressAdmissionLevel;
    matchedPartialIntegrity?: EgressMatchedPartialIntegrity;
    modelAllowlist?: readonly string[];
    blacklist?: EgressAdmissionBlacklist;
  } = { mode };
  if (record['minimumLevel'] !== undefined) {
    if (!isStringEnum(record['minimumLevel'], EGRESS_ADMISSION_LEVELS)) {
      throw new EgressConfigError(
        '$.admission.minimumLevel',
        `must be one of ${EGRESS_ADMISSION_LEVELS.join(', ')}`,
      );
    }
    out.minimumLevel = record['minimumLevel'];
  }
  if (record['matchedPartialIntegrity'] !== undefined) {
    out.matchedPartialIntegrity = parseMatchedPartialIntegrity(record['matchedPartialIntegrity']);
  }
  // allowPlainAnthropicIdentityKinds / allowPlainAnthropicEntrypointClasses
  // (#1311): retired observability-only allow lists that never influenced an
  // admission decision. Stored documents may still carry them; the strict
  // unknown-key rejection would brick those, so both keys stay tolerated and
  // are dropped — any value — instead of parsed or re-emitted.
  if (record['modelAllowlist'] !== undefined) {
    out.modelAllowlist = parseNonEmptyStringArray(
      record['modelAllowlist'],
      '$.admission.modelAllowlist',
    );
  }
  if (record['blacklist'] !== undefined) {
    if (mode !== 'require-cc-black') {
      // A blacklist under any other mode is a rule set nothing reads. Storing
      // it would let an operator tick `bareApiOnCcLane` on a `direct` profile
      // and watch nothing happen, with the document as evidence that it should
      // have. Same posture as `$.target` outside `assemble`.
      throw new EgressConfigError(
        '$.admission.blacklist',
        'is only valid when mode is "require-cc-black"',
      );
    }
    out.blacklist = parseAdmissionBlacklist(record['blacklist']);
  }
  return out;
}

function parseAdmissionBlacklist(value: unknown): EgressAdmissionBlacklist {
  const path = '$.admission.blacklist';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, EGRESS_ADMISSION_BLACKLIST_RULES, path);
  const out: {
    oneTokenMeasurement?: EgressAdmissionBlacklistRule;
    firstPartyHealthProbe?: EgressAdmissionBlacklistRule;
    bareApiOnCcLane?: EgressAdmissionBlacklistRule;
    agentSdkOnCcLane?: EgressAdmissionBlacklistRule;
    missingCcBetaHeader?: EgressAdmissionBlacklistRule;
  } = {};
  for (const rule of EGRESS_ADMISSION_BLACKLIST_RULES) {
    const raw = record[rule];
    if (raw === undefined) continue;
    out[rule] = parseAdmissionBlacklistRule(raw, `${path}.${rule}`);
  }
  // No "must select at least one rule" guard, unlike `$.fixups`: an empty rule
  // set is the shipped default of this mode, not a mistake. `require-cc-black`
  // with nothing ticked has to be a storable, inert configuration — it is what
  // an operator flips to first, and #1609's whole premise is that the flip
  // changes nothing until a rule is armed.
  return out;
}

function parseAdmissionBlacklistRule(value: unknown, path: string): EgressAdmissionBlacklistRule {
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, EGRESS_ADMISSION_BLACKLIST_RULE_KEYS, path);
  if (!isStringEnum(record['action'], EGRESS_ADMISSION_BLACKLIST_ACTIONS)) {
    throw new EgressConfigError(
      `${path}.action`,
      `must be one of ${EGRESS_ADMISSION_BLACKLIST_ACTIONS.join(', ')}`,
    );
  }
  return { action: record['action'] };
}

function parseMatchedPartialIntegrity(value: unknown): EgressMatchedPartialIntegrity {
  const path = '$.admission.matchedPartialIntegrity';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, EGRESS_MATCHED_PARTIAL_KEYS, path);
  const out: {
    targetLevel?: EgressAdmissionLevel;
    requireFingerprintMatch?: boolean;
    requireManifestMatch?: boolean;
    requireNoConflicts?: boolean;
    allowedSurfaceBuckets?: readonly EgressSurfaceBucket[];
    allowedEntrypointClasses?: readonly EgressEntrypointClass[];
    allowedEntrypoints?: readonly string[];
  } = {};
  if (record['targetLevel'] !== undefined) {
    if (!isStringEnum(record['targetLevel'], EGRESS_ADMISSION_LEVELS)) {
      throw new EgressConfigError(
        `${path}.targetLevel`,
        `must be one of ${EGRESS_ADMISSION_LEVELS.join(', ')}`,
      );
    }
    out.targetLevel = record['targetLevel'];
  }
  for (const key of ['requireFingerprintMatch', 'requireManifestMatch', 'requireNoConflicts'] as const) {
    const raw = record[key];
    if (raw !== undefined) {
      if (typeof raw !== 'boolean') {
        throw new EgressConfigError(`${path}.${key}`, 'must be a boolean');
      }
      out[key] = raw;
    }
  }
  if (record['allowedSurfaceBuckets'] !== undefined) {
    out.allowedSurfaceBuckets = parseStringEnumArray(
      record['allowedSurfaceBuckets'],
      EGRESS_SURFACE_BUCKETS,
      `${path}.allowedSurfaceBuckets`,
    );
  }
  if (record['allowedEntrypointClasses'] !== undefined) {
    out.allowedEntrypointClasses = parseStringEnumArray(
      record['allowedEntrypointClasses'],
      EGRESS_ENTRYPOINT_CLASSES,
      `${path}.allowedEntrypointClasses`,
    );
  }
  if (record['allowedEntrypoints'] !== undefined) {
    out.allowedEntrypoints = parseNonEmptyStringArray(
      record['allowedEntrypoints'],
      `${path}.allowedEntrypoints`,
    );
  }
  return out;
}

function parseOpenaiUserMaxLength(value: unknown): number | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 1) {
    throw new EgressConfigError(
      '$.openaiUserMaxLength',
      'must be a positive integer (omit it for no cap)',
    );
  }
  return value;
}

function parseOpenaiPromptCacheKey(value: unknown, lane: EgressLane): boolean | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'boolean') {
    throw new EgressConfigError(
      '$.openaiPromptCacheKey',
      'must be a boolean (omit it to disable)',
    );
  }
  if (lane !== 'openai') {
    throw new EgressConfigError('$.openaiPromptCacheKey', 'is only valid on the openai lane');
  }
  return value;
}

function parseFirewall(value: unknown): EgressFirewallRef | undefined {
  if (value === undefined) return undefined;
  const path = '$.firewall';
  const record = requireRecord(value, path);
  for (const inline of EGRESS_FIREWALL_INLINE_KEYS) {
    if (record[inline] === undefined) continue;
    throw new EgressConfigError(
      `${path}.${inline}`,
      'firewall rulesets can only be referenced by "rulesetId", never inlined: ' +
        'a binding override replaces the whole egress config document, so an inlined ' +
        'chain would be copied per profile, fossilize at the moment of the copy, and ' +
        'detach from the ruleset it was authored against — losing its version history, ' +
        'its validation and its replay',
    );
  }
  assertNoUnknownKeys(record, EGRESS_FIREWALL_KEYS, path);
  const rulesetId = record['rulesetId'];
  if (typeof rulesetId !== 'string' || rulesetId.trim().length === 0) {
    throw new EgressConfigError(`${path}.rulesetId`, 'must be a non-empty ruleset id');
  }
  const version = record['version'];
  if (version === undefined) return { rulesetId };
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new EgressConfigError(
      `${path}.version`,
      'must be a positive integer version (omit it to follow the ruleset\'s active version)',
    );
  }
  return { rulesetId, version };
}

function parseAdapter(value: unknown, lane: EgressLane): EgressAdapterConfig | undefined {
  if (value === undefined) return undefined;
  const record = requireRecord(value, '$.adapter');
  assertNoUnknownKeys(record, EGRESS_ADAPTER_KEYS, '$.adapter');
  if (
    record['codex'] === undefined
    && record['openai'] === undefined
    && record['arkVideo'] === undefined
    && record['grokVideo'] === undefined
    && record['grokImage'] === undefined
  ) {
    throw new EgressConfigError('$.adapter', 'must configure a lane adapter when present');
  }
  if (record['codex'] !== undefined && lane !== 'codex') {
    throw new EgressConfigError('$.adapter.codex', `is only valid on the codex lane, not "${lane}"`);
  }
  if (record['openai'] !== undefined && lane !== 'openai') {
    throw new EgressConfigError('$.adapter.openai', `is only valid on the openai lane, not "${lane}"`);
  }
  if (record['arkVideo'] !== undefined && lane !== 'ark_video_v1') {
    throw new EgressConfigError(
      '$.adapter.arkVideo',
      `is only valid on the ark_video_v1 lane, not "${lane}"`,
    );
  }
  if (record['grokVideo'] !== undefined && lane !== 'grok_video_v1') {
    throw new EgressConfigError(
      '$.adapter.grokVideo',
      `is only valid on the grok_video_v1 lane, not "${lane}"`,
    );
  }
  if (record['grokImage'] !== undefined && lane !== 'grok_image_v1') {
    throw new EgressConfigError(
      '$.adapter.grokImage',
      `is only valid on the grok_image_v1 lane, not "${lane}"`,
    );
  }
  return {
    ...(record['codex'] !== undefined ? { codex: parseCodexAdapter(record['codex']) } : {}),
    ...(record['openai'] !== undefined ? { openai: parseOpenAIAdapter(record['openai']) } : {}),
    ...(record['arkVideo'] !== undefined
      ? { arkVideo: parseArkVideoAdapter(record['arkVideo']) }
      : {}),
    ...(record['grokVideo'] !== undefined
      ? { grokVideo: parseGrokVideoAdapter(record['grokVideo']) }
      : {}),
    ...(record['grokImage'] !== undefined
      ? { grokImage: parseGrokImageAdapter(record['grokImage']) }
      : {}),
  };
}

function parseDeliveryQuantityCoefficient(value: unknown, path: string): number {
  // Strictly positive and finite. Zero is refused rather than stored: a
  // stored zero would bill every delivered generation at nothing, which is
  // the silent give-away the metering feature exists to prevent — an
  // operator who wants a lane unbilled has real levers (delist the SKU),
  // not a coefficient that looks like calibration.
  if (typeof value !== 'number' || !Number.isFinite(value) || value <= 0) {
    throw new EgressConfigError(
      path,
      'must be a finite number greater than 0 (omit it for the 1.0 pass-through)',
    );
  }
  return value;
}

function parseArkVideoAdapter(value: unknown): ArkVideoAdapterConfig {
  const path = '$.adapter.arkVideo';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, ARK_VIDEO_ADAPTER_KEYS, path);
  if (!isStringEnum(record['vendor'], ARK_VIDEO_VENDOR_ADAPTER_IDS)) {
    throw new EgressConfigError(
      `${path}.vendor`,
      `must be one of ${ARK_VIDEO_VENDOR_ADAPTER_IDS.join(', ')}`,
    );
  }
  const out: {
    vendor: ArkVideoVendorAdapterId;
    deliveryQuantityCoefficient?: number;
    parameterSpellings?: Readonly<Record<string, Readonly<Record<string, string>>>>;
    assetRecovery?: boolean;
  } = { vendor: record['vendor'] };
  if (record['deliveryQuantityCoefficient'] !== undefined) {
    out.deliveryQuantityCoefficient = parseDeliveryQuantityCoefficient(
      record['deliveryQuantityCoefficient'],
      `${path}.deliveryQuantityCoefficient`,
    );
  }
  if (record['parameterSpellings'] !== undefined) {
    out.parameterSpellings = parseArkParameterSpellings(
      record['parameterSpellings'],
      `${path}.parameterSpellings`,
    );
  }
  if (record['assetRecovery'] !== undefined) {
    if (typeof record['assetRecovery'] !== 'boolean') {
      throw new EgressConfigError(`${path}.assetRecovery`, 'must be a boolean');
    }
    if (record['assetRecovery'] === true && record['vendor'] !== 'jojokey') {
      throw new EgressConfigError(
        `${path}.assetRecovery`,
        'is only valid when vendor is "jojokey"',
      );
    }
    out.assetRecovery = record['assetRecovery'];
  }
  return out;
}

function parseGrokVideoAdapter(value: unknown): GrokVideoAdapterConfig {
  const path = '$.adapter.grokVideo';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, GROK_VIDEO_ADAPTER_KEYS, path);
  if (!isStringEnum(record['vendor'], GROK_VIDEO_VENDOR_ADAPTER_IDS)) {
    throw new EgressConfigError(
      `${path}.vendor`,
      `must be one of ${GROK_VIDEO_VENDOR_ADAPTER_IDS.join(', ')}`,
    );
  }
  const out: {
    vendor: GrokVideoVendorAdapterId;
    deliveryQuantityCoefficient?: number;
  } = { vendor: record['vendor'] };
  if (record['deliveryQuantityCoefficient'] !== undefined) {
    out.deliveryQuantityCoefficient = parseDeliveryQuantityCoefficient(
      record['deliveryQuantityCoefficient'],
      `${path}.deliveryQuantityCoefficient`,
    );
  }
  return out;
}

function parseGrokImageAdapter(value: unknown): GrokImageAdapterConfig {
  const path = '$.adapter.grokImage';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, GROK_IMAGE_ADAPTER_KEYS, path);
  if (!isStringEnum(record['vendor'], GROK_IMAGE_VENDOR_ADAPTER_IDS)) {
    throw new EgressConfigError(
      `${path}.vendor`,
      `must be one of ${GROK_IMAGE_VENDOR_ADAPTER_IDS.join(', ')}`,
    );
  }
  return { vendor: record['vendor'] };
}

/**
 * The canonical-slot → upstream-spelling map: `{ "<canonical parameter>":
 * { "<canonical value>": "<upstream spelling>" } }`. An empty object is a
 * storable pass-through, matching the blacklist's "present with nothing
 * armed" precedent — it is what an operator lands on before the first
 * respelling is measured.
 */
function parseArkParameterSpellings(
  value: unknown,
  path: string,
): Readonly<Record<string, Readonly<Record<string, string>>>> {
  const record = requireRecord(value, path);
  const out: Record<string, Readonly<Record<string, string>>> = {};
  for (const [parameter, spellings] of Object.entries(record)) {
    if (parameter.length === 0) {
      throw new EgressConfigError(path, 'parameter names must be non-empty strings');
    }
    const entryPath = `${path}.${parameter}`;
    const entries = requireRecord(spellings, entryPath);
    const mapped: Record<string, string> = {};
    for (const [canonical, spelling] of Object.entries(entries)) {
      if (canonical.length === 0) {
        throw new EgressConfigError(entryPath, 'canonical values must be non-empty strings');
      }
      if (typeof spelling !== 'string' || spelling.length === 0) {
        throw new EgressConfigError(
          `${entryPath}.${canonical}`,
          'must map to a non-empty upstream spelling',
        );
      }
      mapped[canonical] = spelling;
    }
    out[parameter] = mapped;
  }
  return out;
}

function parseOpenAIAdapter(value: unknown): OpenAIAdapterConfig {
  const path = '$.adapter.openai';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, OPENAI_ADAPTER_KEYS, path);
  if (record['images'] !== undefined && !isStringEnum(record['images'], OPENAI_ADAPTER_IMAGES_EGRESS)) {
    throw new EgressConfigError(
      `${path}.images`,
      `must be one of ${OPENAI_ADAPTER_IMAGES_EGRESS.join(', ')}`,
    );
  }
  if (record['vendor'] !== undefined && !isStringEnum(record['vendor'], UPSTREAM_VENDOR_ADAPTER_IDS)) {
    throw new EgressConfigError(
      `${path}.vendor`,
      `must be one of ${UPSTREAM_VENDOR_ADAPTER_IDS.join(', ')}`,
    );
  }
  return {
    ...(record['images'] !== undefined ? { images: record['images'] } : {}),
    ...(record['vendor'] !== undefined ? { vendor: record['vendor'] } : {}),
    ...(record['dropRequestFields'] !== undefined
      ? { dropRequestFields: parseOpenAIDropRequestFields(record['dropRequestFields']) }
      : {}),
    ...(record['translateResponsesToChat'] !== undefined
      ? { translateResponsesToChat: parseOpenAITranslateResponsesToChat(record['translateResponsesToChat']) }
      : {}),
    ...(record['repairToolCallArguments'] !== undefined
      ? { repairToolCallArguments: parseOpenAIRepairToolCallArguments(record['repairToolCallArguments']) }
      : {}),
    ...(record['repairForcedThinkingDisable'] !== undefined
      ? {
        repairForcedThinkingDisable: parseOpenAIRepairForcedThinkingDisable(
          record['repairForcedThinkingDisable'],
        ),
      }
      : {}),
  };
}

function parseOpenAITranslateResponsesToChat(value: unknown): boolean {
  const path = '$.adapter.openai.translateResponsesToChat';
  if (typeof value !== 'boolean') {
    throw new EgressConfigError(path, 'must be a boolean');
  }
  return value;
}

function parseOpenAIRepairToolCallArguments(value: unknown): boolean {
  const path = '$.adapter.openai.repairToolCallArguments';
  if (typeof value !== 'boolean') {
    throw new EgressConfigError(path, 'must be a boolean');
  }
  return value;
}

function parseOpenAIRepairForcedThinkingDisable(value: unknown): OpenAIRepairForcedThinkingDisable {
  const path = '$.adapter.openai.repairForcedThinkingDisable';
  if (Array.isArray(value)) {
    throw new EgressConfigError(
      path,
      'must be { models, ratio }; a bare model list is not accepted',
    );
  }
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, OPENAI_REPAIR_FORCED_THINKING_DISABLE_KEYS, path);
  if (!Array.isArray(record['models'])) {
    throw new EgressConfigError(`${path}.models`, 'must be an array of outbound model names');
  }
  const modelsValue = record['models'];
  if (
    modelsValue.length < OPENAI_REPAIR_FORCED_THINKING_DISABLE_MIN
    || modelsValue.length > OPENAI_REPAIR_FORCED_THINKING_DISABLE_MAX
  ) {
    throw new EgressConfigError(
      `${path}.models`,
      `must contain between ${OPENAI_REPAIR_FORCED_THINKING_DISABLE_MIN} and ${OPENAI_REPAIR_FORCED_THINKING_DISABLE_MAX} model names`,
    );
  }
  const seen = new Set<string>();
  const models: string[] = [];
  for (let index = 0; index < modelsValue.length; index += 1) {
    const item = modelsValue[index];
    if (typeof item !== 'string' || !OPENAI_REPAIR_FORCED_THINKING_DISABLE_MODEL.test(item)) {
      throw new EgressConfigError(
        `${path}.models[${index}]`,
        'must be an outbound model name of [A-Za-z0-9._/-] (e.g. "glm-5.3")',
      );
    }
    if (seen.has(item)) continue;
    seen.add(item);
    models.push(item);
  }
  const ratio = record['ratio'];
  if (typeof ratio !== 'number' || !Number.isFinite(ratio) || ratio < 0 || ratio > 1) {
    throw new EgressConfigError(`${path}.ratio`, 'must be a finite number in [0, 1]');
  }
  return { models, ratio };
}

function parseOpenAIDropRequestFields(value: unknown): readonly string[] {
  const path = '$.adapter.openai.dropRequestFields';
  if (!Array.isArray(value)) {
    throw new EgressConfigError(path, 'must be an array of dotted field paths');
  }
  if (value.length < OPENAI_DROP_REQUEST_FIELDS_MIN || value.length > OPENAI_DROP_REQUEST_FIELDS_MAX) {
    throw new EgressConfigError(
      path,
      `must contain between ${OPENAI_DROP_REQUEST_FIELDS_MIN} and ${OPENAI_DROP_REQUEST_FIELDS_MAX} paths`,
    );
  }
  const seen = new Set<string>();
  const out: string[] = [];
  for (let index = 0; index < value.length; index += 1) {
    const item = value[index];
    if (typeof item !== 'string' || !OPENAI_DROP_REQUEST_FIELD_PATH.test(item)) {
      throw new EgressConfigError(
        `${path}[${index}]`,
        'must be a dotted path of [A-Za-z0-9_]+ segments (e.g. "reasoning.summary")',
      );
    }
    if (seen.has(item)) continue;
    seen.add(item);
    out.push(item);
  }
  return out;
}

function parseCodexAdapter(value: unknown): CodexAdapterConfig {
  const path = '$.adapter.codex';
  const record = requireRecord(value, path);
  assertNoUnknownKeys(record, CODEX_ADAPTER_KEYS, path);
  if (!isStringEnum(record['backend'], CODEX_ADAPTER_BACKENDS)) {
    throw new EgressConfigError(
      `${path}.backend`,
      `must be one of ${CODEX_ADAPTER_BACKENDS.join(', ')}`,
    );
  }
  if (!isStringEnum(record['transport'], CODEX_ADAPTER_TRANSPORTS)) {
    throw new EgressConfigError(
      `${path}.transport`,
      `must be one of ${CODEX_ADAPTER_TRANSPORTS.join(', ')}`,
    );
  }
  const out: {
    backend: CodexAdapterBackend;
    transport: CodexAdapterTransport;
    store?: boolean;
    betaFeatures?: readonly string[];
    originatorOverride?: string;
    dropMaxOutputTokens?: boolean;
    dropUnresolvableReasoningItems?: boolean;
    baseURL?: string;
  } = {
    backend: record['backend'],
    transport: record['transport'],
  };
  if (record['store'] !== undefined) {
    if (typeof record['store'] !== 'boolean') {
      throw new EgressConfigError(`${path}.store`, 'must be a boolean');
    }
    out.store = record['store'];
  }
  if (record['betaFeatures'] !== undefined) {
    out.betaFeatures = parseNonEmptyStringArray(record['betaFeatures'], `${path}.betaFeatures`);
  }
  if (record['originatorOverride'] !== undefined) {
    if (typeof record['originatorOverride'] !== 'string' || record['originatorOverride'].length === 0) {
      throw new EgressConfigError(`${path}.originatorOverride`, 'must be a non-empty string');
    }
    out.originatorOverride = record['originatorOverride'];
  }
  // `requestCompression` (#1310): retired without ever gaining a runtime
  // consumer. Stored documents may still carry it; the strict unknown-key
  // rejection would brick them, so the key stays tolerated here and is
  // dropped — any value — instead of parsed or re-emitted.
  if (record['dropMaxOutputTokens'] !== undefined) {
    if (typeof record['dropMaxOutputTokens'] !== 'boolean') {
      throw new EgressConfigError(`${path}.dropMaxOutputTokens`, 'must be a boolean');
    }
    out.dropMaxOutputTokens = record['dropMaxOutputTokens'];
  }
  if (record['dropUnresolvableReasoningItems'] !== undefined) {
    if (typeof record['dropUnresolvableReasoningItems'] !== 'boolean') {
      throw new EgressConfigError(`${path}.dropUnresolvableReasoningItems`, 'must be a boolean');
    }
    out.dropUnresolvableReasoningItems = record['dropUnresolvableReasoningItems'];
  }
  if (record['baseURL'] !== undefined) {
    if (typeof record['baseURL'] !== 'string' || record['baseURL'].length === 0) {
      throw new EgressConfigError(`${path}.baseURL`, 'must be a non-empty string');
    }
    out.baseURL = record['baseURL'];
  }
  return out;
}

function requireRecord(value: unknown, path: string): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    throw new EgressConfigError(path, 'must be a JSON object');
  }
  return value as Record<string, unknown>;
}

function assertNoUnknownKeys(
  record: Record<string, unknown>,
  allowed: readonly string[],
  path: string,
): void {
  for (const key of Object.keys(record)) {
    if (!allowed.includes(key)) {
      throw new EgressConfigError(
        path === '$' ? `$.${key}` : `${path}.${key}`,
        `unknown key "${key}" (allowed: ${allowed.join(', ')})`,
      );
    }
  }
}

function isStringEnum<T extends string>(value: unknown, allowed: readonly T[]): value is T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value);
}

function parseStringEnumArray<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): readonly T[] {
  if (!Array.isArray(value)) {
    throw new EgressConfigError(path, 'must be an array');
  }
  return value.map((item, index) => {
    if (!isStringEnum(item, allowed)) {
      throw new EgressConfigError(`${path}[${index}]`, `must be one of ${allowed.join(', ')}`);
    }
    return item;
  });
}

function parseNonEmptyStringArray(value: unknown, path: string): readonly string[] {
  if (!Array.isArray(value)) {
    throw new EgressConfigError(path, 'must be an array');
  }
  return value.map((item, index) => {
    if (typeof item !== 'string' || item.length === 0) {
      throw new EgressConfigError(`${path}[${index}]`, 'must be a non-empty string');
    }
    return item;
  });
}
